import type { RouteReportSnapshotWithId } from './useRouteReportSnapshots';

/**
 * `RouteReportDashboardPage` の正規化・偏差値比較 (docs/25 §10-6)。
 *
 * 「電線」のように道路上でほぼ均一に出現するカテゴリがあると、路線が長いほど検出件数が
 * 単純に多くなり、路線間の比較が「路線の長さの差」に引きずられてしまう。当初 (MVP) は検出件数を
 * 路線距離で正規化 (100mあたり件数) して対応していたが、候補点の生成間隔 (`intervalM`、
 * `StreetSurveyBatchPanel` で5〜500mの範囲でユーザーが自由に設定可能) が路線ごとに異なると、
 * 間隔を狭く設定した路線ほど同じ距離でも地点数が増え、検出件数・100mあたり件数がその分
 * 単純に多くなってしまう不公平が発覚した（ユーザー指摘、2026-07-23）。§10-5のエリア比較
 * ダッシュボードが「地点数が多いエリアほど検出件数が単純に増える」問題を地点数で正規化して
 * 解決したのと同種の問題のため、路線調査も**主指標を「1地点あたり平均検出件数」に切り替え、
 * 偏差値もこれで算出する**（エリア比較と同じ方式に統一）。距離正規化 (100mあたり件数) は
 * 「同じ距離を歩いたときの体感密度」の参考値として残す（比較対象の路線が全て同じ間隔で
 * 生成されている場合は距離正規化の方が実態に近いため）。基準値（平均）は選択した路線群の
 * 合算から算出する MVP方式（地区の道路網総延長を使う精緻な方式は今回のスコープ外、docs/97
 * に将来拡張として記録）。カテゴリ別ではなく総検出件数ベースの単一の密度指標として算出する
 * （カテゴリ別偏差値も将来拡張）。
 */
export interface RouteDensity {
  id: string;
  name: string;
  lengthM: number;
  pointCount: number;
  totalCount: number;
  /** 参考値: 100mあたり件数 (候補点の生成間隔が路線間で異なると歪む、上記コメント参照)。 */
  densityPer100m: number;
  /** 主指標: 1地点あたり平均検出件数 (地点数で正規化、pointCount=0 は0)。 */
  perPointAvg: number;
}

/** 各路線の総検出件数 (axisCounts合算)、100mあたり件数 (参考値)、1地点あたり平均件数 (主指標) を算出する。 */
export function computeRouteDensities(routes: RouteReportSnapshotWithId[]): RouteDensity[] {
  return routes.map((r) => {
    const totalCount = r.axisCounts.reduce((sum, a) => sum + a.value, 0);
    const densityPer100m = r.lengthM > 0 ? (totalCount / r.lengthM) * 100 : 0;
    const perPointAvg = r.pointCount > 0 ? totalCount / r.pointCount : 0;
    return {
      id: r.id,
      name: r.name,
      lengthM: r.lengthM,
      pointCount: r.pointCount,
      totalCount,
      densityPer100m,
      perPointAvg,
    };
  });
}

export interface RouteCategorySegment {
  key: string;
  label: string;
  color: string;
  /** 参考値: 100mあたりの検出件数 (このカテゴリぶんのみ、上記コメントの間隔差の影響を受ける)。 */
  valuePer100m: number;
  /** 主指標: 1地点あたり平均検出件数 (このカテゴリぶんのみ、pointCount=0 は0)。 */
  valuePerPoint: number;
}

export interface RouteCategoryDensityRow {
  id: string;
  name: string;
  segments: RouteCategorySegment[];
}

/**
 * 各路線のカテゴリ (役割) 別検出件数を、100mあたり件数 (参考値) と1地点あたり平均件数 (主指標)
 * の両方に正規化する。比較グラフを検出要素ごとに色分けする (積み上げ棒グラフ) ための入力データ
 * — 保存時点の `axisCounts` (`key`/`label`/`color`、`computeAxisCounts.ts` が地区のマスタから
 * 動的に決めた色をそのまま使う) をカテゴリ単位で正規化するだけで、`computeRouteDensities` の
 * 合計値 (`densityPer100m`/`perPointAvg`) はこの内訳の合算にそれぞれ一致する。
 */
export function computeRouteCategoryDensities(
  routes: RouteReportSnapshotWithId[],
): RouteCategoryDensityRow[] {
  return routes.map((r) => ({
    id: r.id,
    name: r.name,
    segments: r.axisCounts.map((a) => ({
      key: a.key,
      label: a.label,
      color: a.color,
      valuePer100m: r.lengthM > 0 ? (a.value / r.lengthM) * 100 : 0,
      valuePerPoint: r.pointCount > 0 ? a.value / r.pointCount : 0,
    })),
  }));
}

/**
 * 総合偏差値の「質」に使う指標 (docs/98 R-98 中、2026-09-16)。
 * - `positive-ratio`: 評価の肯定の割合 (肯定 ÷ (肯定 + 否定)、中立は数えない)。評価の軸があるときの既定
 * - `detections-per-point`: 1 地点あたり平均検出件数 (検出が多いほど高い。防災の危険箇所のように
 *   検出が多いほど良くない調査では、この指標を「良さ」として読まない)
 */
export type RouteQualityMetric = 'positive-ratio' | 'detections-per-point';

/** 評価の内訳の最小形。 */
interface PolarityCountsLike {
  positive: number;
  negative: number;
  neutral: number;
}

/**
 * 路線ごとの「質」の値。`positive-ratio` で肯定と否定が 0 件の路線は、比較から外さず平均的な値 (0.5) にする。
 */
export function computeRouteQualityValues(
  routes: readonly { pointCount: number; axisCounts: { value: number }[]; evaluation?: unknown }[],
  metric: RouteQualityMetric,
): number[] {
  return routes.map((r) => {
    if (metric === 'positive-ratio') {
      const ev = r.evaluation as PolarityCountsLike | null | undefined;
      const judged = ev ? ev.positive + ev.negative : 0;
      return judged > 0 ? ev!.positive / judged : 0.5;
    }
    const total = r.axisCounts.reduce((sum, a) => sum + a.value, 0);
    return r.pointCount > 0 ? total / r.pointCount : 0;
  });
}

/**
 * 偏差値 (50 + 10*(x-平均)/標準偏差、母集団標準偏差) を算出する。
 * 全件同値 (標準偏差=0) の場合は全件50を返す。空配列は空配列を返す。
 */
export function computeHensachi(values: number[]): number[] {
  if (values.length === 0) return [];
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  const stddev = Math.sqrt(variance);
  if (stddev === 0) return values.map(() => 50);
  return values.map((v) => 50 + (10 * (v - mean)) / stddev);
}

/**
 * 「質」(1地点あたり平均検出件数) と「量」(距離) それぞれの偏差値を、ユーザー調整可能な重み
 * (`distanceWeight`、0〜1) で合成した「総合偏差値」を算出する (docs/25 §10-6、2026-07-23追加)。
 *
 * 主指標を1地点あたり平均検出件数 (質) に切り替えたところ、「距離も大事」という指摘を受けた
 * — 67mの路地と204mの路線が同じ質の偏差値になってしまうのは、路線としての規模の違いを
 * 無視しているという指摘。距離だけの正規化 (100mあたり件数) には地点生成間隔の歪みがあり
 * 単純に戻せないため、質の偏差値と量 (距離) の偏差値を別々に算出した上で線形合成する方式を
 * 採用した。`distanceWeight=0` で質のみ (現行の主指標と同じ)、`distanceWeight=1` で量のみ
 * (単純な距離順位) になる。既存の「近接判定の閾値」と同じ「ユーザーがその場で重みを調整できる」
 * UIパターンを踏襲する。
 *
 * `qualityValues`/`scaleValues` は同じ路線群・同じ順序である前提 (呼出側で揃える)。
 */
export function computeCombinedHensachi(
  qualityValues: number[],
  scaleValues: number[],
  distanceWeight: number,
): number[] {
  const qualityHensachi = computeHensachi(qualityValues);
  const scaleHensachi = computeHensachi(scaleValues);
  return qualityHensachi.map(
    (q, i) => (1 - distanceWeight) * q + distanceWeight * (scaleHensachi[i] ?? 50),
  );
}
